import { cva, type VariantProps } from "class-variance-authority";
import type * as React from "react";

import { cn } from "../../lib/utils";

/**
 * A filter or choice chip (Library's "All 6 · Designs 1", Notifications' sources, a form's
 * multiple choice). The chosen one sits on the control surface with a hairline
 * (`SELECTED_CONTROL`, written out here so Tailwind sees it); it is never filled white, which
 * stays the screen's one primary button.
 */
const chipVariants = cva(
	"inline-flex shrink-0 items-center justify-center gap-1.5 rounded-control border font-medium whitespace-nowrap transition-colors duration-ui outline-none focus-visible:ring-[length:var(--focus-ring-width)] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 [&_svg]:size-4 [&_svg]:shrink-0",
	{
		variants: {
			size: {
				xs: "type-meta h-control-sm px-2.5",
				sm: "type-ui h-control-sm px-2.5",
				md: "type-ui h-control-md px-3",
			},
			selected: {
				true: "border-input bg-control text-foreground",
				false:
					"border-transparent bg-transparent text-muted-foreground hover:bg-control hover:text-foreground",
			},
		},
		defaultVariants: { size: "sm", selected: false },
	},
);

function Chip({
	className,
	selected = false,
	size,
	role,
	...props
}: Omit<React.ComponentProps<"button">, "aria-pressed" | "aria-selected"> &
	Omit<VariantProps<typeof chipVariants>, "selected"> & { selected?: boolean }) {
	// A chip in a tab list reports `aria-selected`; any other chip is a toggle button.
	const state = role === "tab" ? { "aria-selected": selected } : { "aria-pressed": selected };
	return (
		<button
			data-slot="chip"
			data-state={selected ? "on" : "off"}
			className={cn(chipVariants({ size, selected, className }))}
			role={role}
			type="button"
			{...state}
			{...props}
		/>
	);
}

export { Chip, chipVariants };
